<!--
  Receipt test bench (Tests group only): sample Costco receipt photos run through the model on
  your computer, each checked line by line against its answer key (item numbers, prices, coupons,
  tax letters, the date and the total). Open one to review and add it like any scan.
-->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { checkReading, type ReadingCheck, type ReceiptDraft, type ReceiptKey } from "@split/core";
  import { receiptScanner } from "../lib/ai/scan.svelte.ts";
  import { assistant } from "../lib/ai/assistant.svelte.ts";
  import { link } from "../lib/router.svelte.ts";

  let { onreview }: { onreview: (drafts: ReceiptDraft[], photo: Blob) => void } = $props();

  interface Sample {
    id: string;
    label: string;
  }
  let samples = $state<Sample[]>([]);
  let results = $state<Record<string, { check: ReadingCheck; drafts: ReceiptDraft[]; photo: Blob; ms: number } | { error: string }>>({});
  let running = $state<string | null>(null);
  let open = $state(true);
  let loadError = $state("");

  $effect(() => {
    if (!open || samples.length) return;
    fetch("/test-receipts/index.json", { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`the sample list didn't load (${r.status})`);
        return r.json();
      })
      .then((x: Sample[]) => (samples = x))
      .catch((e) => (loadError = `Couldn't load the samples: ${(e as Error).message}.`));
    if (assistant.connection.status !== "ok") void assistant.check();
  });

  async function run(s: Sample) {
    running = s.id;
    try {
      const get = async (path: string) => {
        const r = await fetch(path, { cache: "no-store" });
        if (!r.ok) throw new Error(`Couldn't load ${path} (${r.status}).`);
        return r;
      };
      const [photo, key] = await Promise.all([
        get(`/test-receipts/${s.id}.jpg`).then((r) => r.blob()),
        get(`/test-receipts/${s.id}.json`).then((r) => r.json() as Promise<ReceiptKey>),
      ]);
      const t0 = performance.now();
      // Each sample is one receipt, so it's checked against the first one read; the review gets all
      // of them, like any scan.
      const drafts = await receiptScanner.scan(photo);
      const ms = performance.now() - t0;
      results[s.id] = drafts[0] ? { check: checkReading(drafts[0], key), drafts, photo, ms } : { error: receiptScanner.error || "No reading came back." };
    } catch (e) {
      results[s.id] = { error: (e as Error).message };
    } finally {
      running = null;
    }
  }
  async function runAll() {
    for (const s of samples) await run(s);
  }
  const average = $derived.by(() => {
    const done = Object.values(results).filter((r): r is { check: ReadingCheck; drafts: ReceiptDraft[]; photo: Blob; ms: number } => "check" in r);
    return done.length ? Math.round(done.reduce((t, r) => t + r.check.score, 0) / done.length) : null;
  });
</script>

<section class="bench">
  <button class="head" onclick={() => (open = !open)} aria-expanded={open}>
    <strong>Test receipt reading</strong>
    <span class="muted small">Sample Costco receipts, read by the model on your computer and checked line by line{average !== null ? ` · average ${average}%` : ""}</span>
  </button>
  {#if open}
    {#if assistant.connection.status === "down"}
      <p class="owes small">{assistant.connection.message || "Can't reach LM Studio."} <a href="/ask" use:link>Set it up on Ask</a>.</p>
    {:else if assistant.connection.status === "ok"}
      <p class="muted small">Reading with {assistant.settings.model || assistant.connection.models[0] || "the loaded model"}.</p>
    {/if}
    {#if loadError}<p class="owes small">{loadError}</p>{/if}
    <div class="btns">
      <button class="btn small primary" onclick={runAll} disabled={running !== null || !samples.length}>Read all {samples.length}</button>
      <a class="btn small quiet" href="/test-receipts/costco-medium.jpg" target="_blank" rel="noopener">See a sample photo</a>
    </div>
    <ul class="list">
      {#each samples as s (s.id)}
        {@const r = results[s.id]}
        <li>
          <span class="name">{s.label}</span>
          {#if running === s.id}
            <span class="muted small">Reading…</span>
          {:else if r && "check" in r}
            <span class="score" class:good={r.check.score >= 90} class:ok={r.check.score >= 70 && r.check.score < 90}>{r.check.score}%</span>
            <span class="muted small detail">
              {r.check.found}/{r.check.lines} lines, {r.check.prices} prices, {r.check.coupons} coupons, {r.check.taxFlags} tax letters right ·
              date {r.check.dateRight ? "right" : "wrong"} · total {r.check.totalRight ? "right" : "wrong"} · card {r.check.cardRight ? "right" : "wrong"} ·
              {(r.ms / 1000).toFixed(0)}s{#if r.drafts.length !== 1} · read as {r.drafts.length} receipts{/if}
              {#if r.check.missed.length}<br />Missed {r.check.missed.join(", ")}{/if}
              {#if r.check.extra.length}<br />Misread or made up {r.check.extra.join(", ")}{/if}
            </span>
            <span class="row-btns">
              <button class="btn small" onclick={() => onreview(r.drafts, r.photo)}>Review and add</button>
              <button class="btn small quiet" onclick={() => run(s)} disabled={running !== null}>Read again</button>
            </span>
          {:else if r && "error" in r}
            <span class="owes small">{r.error}</span>
            <button class="btn small" onclick={() => run(s)} disabled={running !== null}>Try again</button>
          {:else}
            <button class="btn small" onclick={() => run(s)} disabled={running !== null}>Read it</button>
          {/if}
        </li>
      {/each}
    </ul>
    {#if H.RECEIPT_TESTS_NOTE.trim()}<p class="muted small">{H.RECEIPT_TESTS_NOTE}</p>{/if}
  {/if}
</section>

<style>
  .bench {
    display: grid;
    gap: 10px;
    padding: 14px 16px;
    margin-bottom: 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
  }

  .head {
    display: grid;
    gap: 2px;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .btns {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .list {
    display: grid;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .list li {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 6px 12px;
    align-items: center;
    padding: 8px 0;
    border-top: 1px solid var(--rule);
  }

  .row-btns {
    display: flex;
    gap: 6px;
  }

  .detail {
    grid-column: 1 / -1;
  }

  .name {
    font-weight: 600;
  }

  .score {
    font-weight: 750;
    color: var(--owes, #c0392b);
    font-variant-numeric: tabular-nums;
  }

  .score.ok {
    color: #b8860b;
  }

  .score.good {
    color: var(--ok);
  }

  .small {
    font-size: var(--t-xs);
  }
</style>
